import type { ReactNode } from 'react';
import { ArrowUpRight, type LucideIcon } from 'lucide-react';
import { cx } from './cx';

interface KpiCardProps {
  label: string;
  icon?: LucideIcon;
  value: ReactNode;
  /** Unité ou complément discret après la valeur (« h », « / 20 »…). */
  unit?: string;
  foot?: ReactNode;
  /** Rend la carte cliquable (ouvre le détail). */
  onClick?: () => void;
  children?: ReactNode;
}

/** Chiffre clé. */
export function KpiCard({ label, icon: Icon, value, unit, foot, onClick, children }: KpiCardProps) {
  const body = (
    <>
      <span className="flex items-center gap-2 text-[13px] text-secondary">
        {Icon && <Icon size={16} strokeWidth={1.9} className="text-primary" aria-hidden />}
        {label}
      </span>
      <span className="text-[30px] font-semibold leading-[1.1] tracking-[-1.4px] text-heading tabular-nums max-sm:text-[24px]">
        {value}
        {unit && <small className="ml-0.5 text-[15px] font-medium tracking-[-.3px] text-muted">{unit}</small>}
      </span>
      {children}
      {foot && <span className="text-xs text-muted max-sm:text-[11px] max-sm:leading-tight">{foot}</span>}
    </>
  );
  const classes = 'relative flex flex-col gap-1.5 rounded-[20px] bg-white p-5 text-left shadow-card transition-all duration-200 max-sm:gap-1 max-sm:p-3.5';
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        classes,
        'group hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_#C7D2FE,rgba(0,0,0,.05)_0_.6px_1.8px_-.75px,rgba(0,0,0,.06)_0_10px_30px_-2.25px]',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100',
      )}
    >
      <ArrowUpRight size={16} className="absolute right-[18px] top-[18px] text-muted group-hover:text-primary" aria-hidden />
      {body}
    </button>
  ) : (
    <div className={classes}>{body}</div>
  );
}

/** Barre de progression fine (dégradé indigo, ou vert si terminé). */
export function ProgressBar({ value, tone = 'primary', label }: { value: number; tone?: 'primary' | 'success'; label: string }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-1.5 overflow-hidden rounded-md bg-surface-2"
    >
      <span
        className={cx('block h-full rounded-md transition-[width] duration-700 ease-enter', tone === 'success' ? 'bg-bar-success' : 'bg-bar-primary')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
